import { useEffect, useMemo, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { startCase } from 'lodash';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import useResponsive from '@/hooks/useResponsive';
import NestedMenuPolicy from '@/layouts/sidebar/NestedMenuPolicy';
import { getHomepageDocument } from '@/services/apis';
import palette from '@/theme/palette';
import { pathPolicy } from '@/utils';

import BigSidebar from '../components/big-sidebar';
import MenuItem from '../components/menu-level';
import Image from 'next/image';
import SearchDocumentSidebar from '@/components/search-document-sidebar';

export default function Sidebar({
  sidebarData,
  isCollapsed = false,
  onToggleCollapse,
  stickyMode,
}) {
  const [isShowAllSidebar, setIsShowAllSidebar] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const { t } = useTranslation(['document']);
  const router = useRouter();
  const theme = useTheme();
  const productName = startCase(router?.query?.productId?.[0]?.replaceAll('-', ' '));
  const rootPath = router?.pathname?.split('/')?.[1];
  const isDesktop = useResponsive('up', 'sm');
  const isPagePolicy = useMemo(() => rootPath === pathPolicy(), [rootPath]);
  const isDocumentRoute = router.pathname.includes('/document');
  const [boardVisible, setBoardVisible] = useState(true);
  const sidebarTitle = isPagePolicy
    ? 'Điều khoản và chính sách'
    : t(isDocumentRoute ? 'guide_page_title' : 'document', { ns: 'document' });

  const getAllService = async () => {
    try {
      const response = await getHomepageDocument();
      setAllServices(response);
    } catch {
      setAllServices([]);
    }
  };

  useEffect(() => {
    if (router?.query?.productId) {
      getAllService();
      if (isShowAllSidebar) {
        setIsShowAllSidebar(true);
      }
    } else {
      setIsShowAllSidebar(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.query?.productId]);

  const menuSideBar = useMemo(() => {
    if (isPagePolicy && isDesktop) {
      return <NestedMenuPolicy data={sidebarData} />;
    }

    if (!isPagePolicy) {
      return sidebarData.map((item, key) => <MenuItem key={key} item={item} />);
    }
    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.query?.productId, sidebarData]);

  let timer = 0;

  const handleHideBigMenu = () => {
    timer = setTimeout(() => {
      setIsShowAllSidebar(false);
    }, 200);
  };

  const handleShowBigMenu = () => {
    clearTimeout(timer);
    if (!isShowAllSidebar) {
      setIsShowAllSidebar(true);
    }
  };

  const onCall = () => {
    window.location.href = 'tel:19002010';
  };

  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        height: { xs: 'auto', md: stickyMode ? '100%' : '100vh' },
        overflowY: 'visible',
        position: 'relative',
        left: 0,
        zIndex: 3,
        px: isCollapsed ? 0.5 : { xs: 1.5, md: 2 },
        pt: { xs: 1.5, md: 2 },
        pb: { xs: 1.5, md: 2 },
        backgroundColor: '#fff',
        borderRight: `1px solid #F2F2F2`,
        overflowX: 'visible',
      }}
    >
      <Box
        sx={{
          px: isCollapsed ? 0 : { xs: 0, md: 1 },
        }}
        onMouseEnter={() => handleShowBigMenu()}
        onMouseLeave={() => handleHideBigMenu()}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          fontSize={16}
          color="#231F20"
          sx={{
            py: 1.5,
            px: 1,
            mb: 1,
            fontSize: 19,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {!isCollapsed && (
              <Box
                sx={{
                  borderBottom: 'solid 1px #F2F2F2',
                  padding: '16px 32px',
                  marginLeft: '-32px',
                  marginRight: '-32px',
                }}
              >
                {sidebarTitle}
              </Box>
            )}
            {isDocumentRoute && onToggleCollapse && (
              <Box
                onClick={onToggleCollapse}
                sx={{
                  cursor: 'pointer',
                  display: { sx: 'none', md: 'block' },
                  position: 'absolute',
                  right: '-18px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <Image
                  width={20}
                  height={80}
                  src={`/images/sidebar-toggle-btn-${isCollapsed ? 'open' : 'close'}.svg`}
                  alt="toggle-btn"
                />
              </Box>
            )}
          </Box>
        </Typography>
        {isDocumentRoute && !isCollapsed && (
          <Box sx={{ mb: 2, mt: 2 }}>
            <SearchDocumentSidebar sidebarData={sidebarData} />
          </Box>
        )}
        {!rootPath && (
          <>
            <Box
              sx={{
                p: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                border: `1px solid ${theme.palette.border.card}`,
              }}
            >
              <Typography
                fontWeight={700}
                fontSize={16}
                sx={{
                  color: theme.palette.text.blue,
                }}
              >
                {productName}
              </Typography>
              <MenuIcon
                size="small"
                sx={{
                  rotate: isShowAllSidebar ? '90deg' : 'inherit',
                  transition: '.2s ease',
                  color: palette.dark.background.blue,
                }}
              />
            </Box>
            <BigSidebar allDocumentTypes={allServices} setIsShowAllSidebar={handleShowBigMenu} />
          </>
        )}
      </Box>

      {/* menu sidebar */}
      {!isCollapsed && (
        <Box
          sx={{
            pl: isDocumentRoute ? 0 : '10px',
            height: {
              xs: 'auto',
              md: `calc(100${stickyMode ? '%' : 'vh'} - 168px - ${
                boardVisible ? '152px' : '0px'
              } - ${!stickyMode ? '86px' : '0px'})`,
            },
            overflowY: 'auto',
          }}
          className="sidebar-document"
        >
          {menuSideBar}
        </Box>
      )}
      {!isCollapsed && boardVisible && (
        <Box
          sx={{
            backgroundColor: '#EFF6FF',
            borderRadius: '8px',
            p: 2,
            backgroundImage: 'url(/images/support-247-bg.png)',
            backgroundSize: '100% 100%, 100% 100%, 100% 100%, 88px 88px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
          }}
        >
          <Box
            sx={{ position: 'absolute', top: '10px', right: '10px', cursor: 'pointer' }}
            onClick={() => setBoardVisible(false)}
          >
            <Image src="/images/close.svg" alt="close" height={10} width={10} />
          </Box>
          <Grid container gap={1}>
            <Grid item xs={3}>
              <Image width={69} height={69} src="/images/mascot.svg" alt="mascot" />
            </Grid>
            <Grid item xs={8.5}>
              <Typography sx={{ color: '#14A8E4', fontWeight: 600, fontSize: 16 }}>
                {t('support_247', { ns: 'common' })}
              </Typography>
              <Typography sx={{ color: '#231F20', fontWeight: 400, fontSize: 14 }}>
                {t('always_ready_to_support', { ns: 'common' })}
              </Typography>
            </Grid>
          </Grid>
          <Box
            onClick={onCall}
            sx={{
              cursor: 'pointer',
              marginTop: '13px',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              border: 'solid 1px #14A8E4',
              boxShadow: '0px 1px 2px 0px #231F200D',
              padding: '8px 12px',
              borderRadius: '8px',
            }}
          >
            <Image width={16} height={16} src="/images/phone-call.svg" alt="call-us-now" />
            <Typography sx={{ marginLeft: '6px', fontWeight: 600, fontSize: 14, color: '#14A8E4' }}>
              {t('contact_now', { ns: 'common' })}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
